import { BEMBlock } from 'helpers/classnames';
import { Button } from 'palette/Button';
import { useHeaderTopContext } from 'palette/HeaderTop/context';
import { TextLink } from 'palette/TextLink';
import { Small, Typography, Header } from 'palette/Typography';
import './styles/Promobar.scss';

interface PromoBarProps {
    onMainLinkClick: React.MouseEventHandler<HTMLElement>;
    onExpandedLinkClick: React.MouseEventHandler<HTMLElement>;
    onOpen(): void;
    onClose(): void;
    onPreviousClick(): void;
    onNextClick(): void;
    ariaLabel?: {
        leftArrowButton: string;
        rightArrowButton: string;
    };
}

function PromoBar({
    onMainLinkClick,
    onExpandedLinkClick,
    onOpen,
    onClose,
    onPreviousClick,
    onNextClick,
    ariaLabel,
}: PromoBarProps) {
    const {
        activeBanner,
        isExpanded,
        setIsExpanded,
        previousBanner,
        nextBanner,
        startInterval,
        stopInterval,
        hasMultipleBanners,
    } = useHeaderTopContext();

    if(!activeBanner) {
        return null;
    }

    const {
        fontColor,
        mainText,
        subText,
        extraSubText,
        minimizedLink = '',
        expandedTitle,
        expandedText,
        links,
        disclaimerText,
        backgroundColor,
    } = activeBanner;
    const hasExpandableTexts = expandedTitle !== '' || expandedText !== '' || disclaimerText !== '';
    const hasMinimizedLink = minimizedLink !== '';
    const hasExpandableContent = (hasExpandableTexts || links.length > 0) && !hasMinimizedLink;
    const [block, BEMElement] = BEMBlock('palette-promobar', {
        expanded: isExpanded,
    });

    const handlePreviousClick = () => {
        previousBanner();
        onPreviousClick();
    };

    const handleNextClick = () => {
        nextBanner();
        onNextClick();
    };

    const handlePromoBarExpand = () => {
        stopInterval();
        setIsExpanded(true);
        onOpen();
    };

    const handlePromoBarCollapse = () => {
        setIsExpanded(false);
        startInterval();
        onClose();
    };

    const handleMainLinkClick: React.MouseEventHandler<HTMLElement> = (event) => {
        onMainLinkClick(event);
    };

    const handleExpandedLinkClick: React.MouseEventHandler<HTMLElement> = (event) => {
        onExpandedLinkClick(event);
    };

    const bannerText = (
        <Small
            className={BEMElement('banner-text')}
            color="inherit"
            level="2"
        >
            <Typography
                transform="uppercase"
                weight="bold"
            >
                {mainText}
            </Typography>
            <Typography className={BEMElement('sub-text')}>
                {subText}
            </Typography>
            <Typography className={BEMElement('sub-text')}>
                {extraSubText}
            </Typography>
        </Small>
    );

    return (
        <>
            <div
                className={block}
                style={{ backgroundColor, color: fontColor }}
            >
                <div className={BEMElement('carousel')}>
                    {hasMultipleBanners ? (
                        <Button
                            isTransparent
                            ariaLabel={ariaLabel?.leftArrowButton}
                            className={BEMElement('previous-button')}
                            iconColor={fontColor}
                            iconName="palette_chevron_left"
                            onClick={handlePreviousClick}
                        />
                    ) : null}
                    <div
                        className={BEMElement('banner')}
                        {...hasExpandableContent && { onClick: handlePromoBarExpand }}
                    >
                        {hasMinimizedLink ? (
                            <TextLink
                                href={minimizedLink}
                                onClick={handleMainLinkClick}
                                onContextMenu={handleMainLinkClick}
                            >
                                {bannerText}
                            </TextLink>
                        ) : bannerText}
                    </div>
                    {hasMultipleBanners ? (
                        <Button
                            isTransparent
                            ariaLabel={ariaLabel?.rightArrowButton}
                            className={BEMElement('next-button')}
                            iconColor={fontColor}
                            iconName="palette_chevron_right"
                            onClick={handleNextClick}
                        />
                    ) : null}
                    <Button
                        isTransparent
                        className={BEMElement('close-button')}
                        iconColor={fontColor}
                        iconName="palette_close"
                        onClick={handlePromoBarCollapse}
                    />
                </div>
                {isExpanded && expandedTitle !== '' ? (
                    <Header
                        className={BEMElement('expanded-title')}
                        color="inherit"
                        level="2"
                    >
                        {expandedTitle}
                    </Header>
                ) : null}
                {isExpanded && expandedText ? (
                    <Typography className={BEMElement('expanded-text')}>
                        {expandedText}
                    </Typography>
                ) : null}
                {links.length > 0 && isExpanded ? (
                    <ul className={BEMElement('expanded-links')}>
                        {links
                            .filter(link => link.name !== '' && link.url !== '')
                            .map(link => (
                                <li
                                    key={link.name}
                                    className={BEMElement('expanded-link-item')}
                                >
                                    <Button
                                        isExpanded
                                        href={link.url}
                                        theme="secondary"
                                        onClick={handleExpandedLinkClick}
                                        onContextMenu={handleExpandedLinkClick}
                                    >
                                        {link.name}
                                    </Button>
                                </li>
                            ))}
                    </ul>
                ) : null}
                {isExpanded && disclaimerText ? (
                    <Typography className={BEMElement('disclaimer-text')}>
                        {disclaimerText}
                    </Typography>
                ) : null}
            </div>
            {isExpanded ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                    className={BEMElement('overlay')}
                    onClick={handlePromoBarCollapse}
                />
            ) : null}
        </>
    );
}

export default PromoBar;
